import ReferenceCurrency from '../components/ReferenceCurrency.jsx';
import { trackItems } from '../analytics/analytics.js';
// src/pages/CheckoutPage.jsx
// Visual base: prototype L9469-9694 (charcoal). WP-3 upgrade:
//   - Method selector: Card / Apple Pay / Google Pay (Stripe) + PayMe / FPS (manual).
//     AlipayHK / WeChat / Octopus NEVER shown (locked #5).
//   - Stripe methods -> createStripeCheckoutSession CF -> redirect to hosted
//     session.url. (Necessary deviation from the prototype's inline demo card
//     fields: Stripe Checkout is hosted/PCI-offloaded, so we redirect rather
//     than collect card data on-page.)
//   - PayMe / FPS -> manual flow (/pack-payment-choice/:packId).
//   - Legal + age/parental consent gate (LegalConsent) feeds acceptedLegalVersions
//     to the CF and records consent via recordLegalConsent (consentMethod 'checkout').
//   - Mock mode has no backend: the pay action is gated with a notice (P7).
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconLock } from '../components/icons.jsx';
import { resolveCheckoutCopy } from '../config/checkout.js';
import LegalConsent from '../components/LegalConsent.jsx';
import PromoCodeBox, { fmtMinorHKD } from '../components/PromoCodeBox.jsx';
import { AUTH_MODE } from '../auth/firebase.js';
import { createStripeCheckoutSession, recordLegalConsent } from '../lib/callables.js';

const STRIPE_METHODS = ['card', 'apple', 'google'];
const PAYMENT_METHODS = [
    { id: 'card', label: 'Credit / Debit Card', brand: 'Visa · Mastercard · Amex' },
    { id: 'apple', label: 'Apple Pay', brand: 'On supported devices' },
    { id: 'google', label: 'Google Pay', brand: 'On supported devices' },
    { id: 'payme', label: 'PayMe', brand: 'Manual · admin-approved' },
    { id: 'fps', label: 'FPS', brand: 'Manual · admin-approved' }
];

export const CheckoutPage = ({ state }) => {
    const navigate = useNavigate();
    const checkout = resolveCheckoutCopy(state.checkoutContent);
    const [form, setForm] = useState({
        name: state.user?.name || '',
        email: state.user?.email || '',
        country: 'HK'
    });
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [consent, setConsent] = useState({ consented: false, versions: {} });
    // Promo code (promo-codes slice): { code, preview } from previewPurchase.
    // Display-only here -- the authoritative charge is always server-derived.
    const [promo, setPromo] = useState(null);

    const total = state.cart.reduce((sum, item) => sum + item.price, 0);
    // Stripe checkout only handles study packs (credit packs via Stripe = WP-9).
    const packIds = state.cart.filter(i => i.type !== 'credit-pack').map(i => i.id);
    const isStripe = STRIPE_METHODS.includes(paymentMethod);
    const isMock = AUTH_MODE === 'mock';

    // Server-derived per-line discount for a cart pack (null when undiscounted).
    const discountFor = (itemId) => {
        if (!promo) return null;
        const line = (promo.preview.items || []).find(it => it.packId === itemId);
        return (line && line.discountAmount > 0) ? line : null;
    };
    // When a promo is applied, the pack subtotal comes from the SERVER preview
    // (never mixed with the possibly-stale cart snapshot prices -- mixing bases
    // can contradict the per-line rows). Credit-packs aren't priced by the
    // preview, so their cart prices (HK$ major) are added on top.
    const creditTotalMajor = state.cart
        .filter(i => i.type === 'credit-pack')
        .reduce((sum, i) => sum + i.price, 0);
    const totalLabel = promo
        ? fmtMinorHKD(promo.preview.totalAmount + creditTotalMajor * 100)
        : 'HK$' + total;
    const wasLabel = promo
        ? fmtMinorHKD(promo.preview.originalTotal + creditTotalMajor * 100)
        : null;

    const handlePay = async () => {
        setError(null);
        if (isMock) {
            setError('Checkout is unavailable in mock mode. Run the app with VITE_AUTH_MODE=emulator or firebase.');
            return;
        }
        if (packIds.length === 0) {
            setError('Your cart has no study packs to check out.');
            return;
        }
        setProcessing(true);
        try {
            // Record the clickwrap consent (boxes already gate this button). The CF expects
            // each consent as { type, documentVersion } (recordLegalConsent.js); consent.versions
            // is the {type: version} map from LegalConsent.
            const consents = Object.entries(consent.versions).map(([type, documentVersion]) => ({ type, documentVersion }));
            if (consents.length > 0) {
                await recordLegalConsent({ consents, consentMethod: 'checkout' });
            }
            if (isStripe) {
                const { url } = await createStripeCheckoutSession({
                    packIds,
                    acceptedLegalVersions: consent.versions,
                    ...(promo ? { promoCode: promo.code } : {})
                });
                if (!url) throw new Error('No checkout URL was returned.');
                trackItems('add_payment_info', state.cart, { payment_type: paymentMethod });
                window.location.href = url; // redirect to Stripe-hosted Checkout
                return;
            }
            // PayMe / FPS -> per-pack manual flow (/pack-payment-choice redirect).
            // Hand the applied code across the navigation, SCOPED to the pack
            // being paid for -- the PayMe page consumes the key on read (one
            // shot) and only auto-applies it to the same pack, so a stale code
            // can never leak onto a later unrelated purchase.
            if (promo) {
                sessionStorage.setItem('btl_promo_code',
                    JSON.stringify({ code: promo.code, packId: packIds[0] }));
            } else {
                sessionStorage.removeItem('btl_promo_code');
            }
            trackItems('add_payment_info', state.cart.filter(p => p.id === packIds[0]), { payment_type: paymentMethod });
            navigate('/pack-payment-choice/' + packIds[0]);
        } catch (e) {
            setError((e && e.message) ? e.message : 'Checkout could not be started.');
            setProcessing(false);
        }
    };

    const selected = PAYMENT_METHODS.find(p => p.id === paymentMethod);
    const payDisabled = processing || state.cart.length === 0 || !consent.consented;

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-charcoal-bg paper-texture page-root-text-charcoal">
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-16">
                <div className="mb-10">
                    <div className="font-display text-charcoal-accent text-xs mb-2 font-semibold">{checkout.pageTagline}</div>
                    <h1 className="font-display text-3xl md:text-5xl text-charcoal-text">{checkout.pageTitle}</h1>
                </div>

                <div className="grid lg:grid-cols-5 gap-6">
                    {/* Left: contact + payment */}
                    <div className="lg:col-span-3 space-y-5">
                        {/* Contact */}
                        <div className="border border-charcoal-accent/20 bg-charcoal-page/60 p-6 text-charcoal-accent">
                            <h3 className="font-display text-charcoal-accent text-xs mb-5 font-semibold">{checkout.contactSectionTitle}</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-display text-charcoal-muted mb-2 font-semibold">Full Name</label>
                                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="w-full bg-charcoal-pageDark border border-charcoal-accent/30 px-3 py-2.5 text-charcoal-text font-serif focus:outline-none focus:border-charcoal-accent" />
                                </div>
                                <div>
                                    <label className="block text-xs font-display text-charcoal-muted mb-2 font-semibold">Email</label>
                                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="w-full bg-charcoal-pageDark border border-charcoal-accent/30 px-3 py-2.5 text-charcoal-text font-serif focus:outline-none focus:border-charcoal-accent" />
                                </div>
                            </div>
                            <div className="mt-4">
                                <label className="block text-xs font-display text-charcoal-muted mb-2 font-semibold">Country / Region</label>
                                <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                                    className="w-full bg-charcoal-pageDark border border-charcoal-accent/30 px-3 py-2.5 text-charcoal-text font-serif focus:outline-none focus:border-charcoal-accent">
                                    <option value="HK">Hong Kong SAR</option>
                                    <option value="SG">Singapore</option>
                                    <option value="UK">United Kingdom</option>
                                    <option value="CN">China</option>
                                    <option value="MO">Macau SAR</option>
                                    <option value="TW">Taiwan</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        {/* Payment */}
                        <div className="border border-charcoal-accent/20 bg-charcoal-page/60 p-6 text-charcoal-accent">
                            <div className="flex justify-between items-center mb-5">
                                <h3 className="font-display text-charcoal-accent text-xs font-semibold">{checkout.paymentSectionTitle}</h3>
                                <div className="flex items-center gap-1.5 font-serif italic text-charcoal-muted text-xs">
                                    <IconLock className="w-3 h-3" />
                                    Secured payment
                                </div>
                            </div>

                            {/* Method selector (same grid design as prototype L9547-9615) */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-5">
                                {PAYMENT_METHODS.map(pm => (
                                    <button key={pm.id} onClick={() => setPaymentMethod(pm.id)} type="button"
                                        className={`p-3 border text-left transition-all ${paymentMethod === pm.id ? 'border-charcoal-accent bg-charcoal-accent/10' : 'border-charcoal-accent/20 hover:border-charcoal-accent/50'}`}>
                                        <div className="font-display text-charcoal-text text-xs truncate font-semibold">{pm.label}</div>
                                        <div className="font-serif italic text-charcoal-muted text-xs truncate font-semibold">{pm.brand}</div>
                                    </button>
                                ))}
                            </div>

                            {/* Redirect / flow notice */}
                            <div className="py-6 px-4 text-center border border-charcoal-accent/20 bg-charcoal-pageDark/50">
                                {isStripe ? (
                                    <p className="font-serif italic text-charcoal-muted">
                                        You'll be securely redirected to <span className="text-charcoal-accent">Stripe</span> to
                                        complete your payment. Card payments are accepted; Apple Pay and Google Pay
                                        appear when available for your device and browser.
                                    </p>
                                ) : (
                                    <p className="font-serif italic text-charcoal-muted">
                                        You'll see <span className="text-charcoal-accent">{selected?.label}</span> payment
                                        instructions and a reference code, then confirm once paid.
                                        Access is granted after Danielle approves.
                                    </p>
                                )}
                            </div>

                            {/* Legal + age/parental consent (extends design language; locked #15) */}
                            <div className="mt-5">
                                <LegalConsent theme="charcoal"
                                    requireTypes={['terms_of_service', 'privacy_policy', 'refund_policy']}
                                    onConsentChange={setConsent} />
                            </div>

                            <p className="font-serif italic text-charcoal-accent/80 text-xs mt-4 text-center">
                                {checkout.billedAnnuallyNote}
                            </p>
                            {isStripe && <p className="font-serif text-charcoal-muted text-xs mt-2 text-center">
                                Prices are set in HKD. Your bank or Stripe may apply a different exchange rate.
                            </p>}
                        </div>
                    </div>

                    {/* Right: order summary */}
                    <div className="lg:col-span-2">
                        <div className="sticky top-24 border border-charcoal-accent/30 bg-charcoal-pageDark p-6 decorative-corner text-charcoal-accent">
                            <h3 className="font-display text-charcoal-accent text-xs mb-5 font-semibold">{checkout.billingSectionTitle}</h3>
                            <div className="space-y-3 mb-5">
                                {state.cart.map(item => {
                                    const disc = discountFor(item.id);
                                    return (
                                        <div key={item.id} className="flex justify-between items-start gap-3 text-sm">
                                            <div className="flex-grow">
                                                {item.type === 'credit-pack' ? (
                                                    <>
                                                        <div className="font-display text-charcoal-text text-xs font-semibold">Tutor Review Credits · {item.label}</div>
                                                        <div className="font-serif italic text-charcoal-muted text-xs">+{item.credits} review credit{item.credits === 1 ? '' : 's'} · permanent</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="font-display text-charcoal-text text-xs font-semibold">{item.label}</div>
                                                        <div className="font-serif italic text-charcoal-muted text-xs">12-month access · includes updates</div>
                                                        {disc && (
                                                            <div className="font-serif italic text-charcoal-accent text-xs">{promo.code} −{promo.preview.discountPercent}%</div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {disc ? (
                                                <div className="text-right flex-shrink-0">
                                                    <div className="font-serif text-charcoal-muted text-xs line-through">{fmtMinorHKD(disc.originalAmount)}</div>
                                                    <div className="font-display text-charcoal-accent text-sm">{fmtMinorHKD(disc.finalAmount)}</div>
                                                </div>
                                            ) : (
                                                <div className="font-display text-charcoal-accent text-sm">HK${item.price}</div>
                                            )}
                                        </div>
                                    );
                                })}
                                {state.cart.length === 0 && (
                                    <p className="font-serif italic text-charcoal-muted text-sm">Your cart is empty.</p>
                                )}
                            </div>
                            {/* Promo code (packs only; server-derived preview) */}
                            {packIds.length > 0 && (
                                <div className="mb-5">
                                    <PromoCodeBox theme="charcoal" packIds={packIds} applied={promo}
                                        onApplied={setPromo} onRemoved={() => setPromo(null)} />
                                </div>
                            )}

                            <div className="flex justify-between items-center py-4 border-t border-charcoal-accent/30">
                                <span className="font-display text-charcoal-text text-sm font-semibold">Total</span>
                                <span className="text-right">
                                    {promo && (
                                        <span className="block font-serif text-charcoal-muted text-sm line-through">{wasLabel}</span>
                                    )}
                                    <span className="font-display text-charcoal-accent text-2xl">{totalLabel}</span>
                                </span>
                            </div>

                            <ReferenceCurrency amount={promo ? promo.preview.totalAmount / 100 + creditTotalMajor : total} />
                            {error && (
                                <p className="mt-4 font-serif text-red-300 text-sm border border-red-500/40 bg-red-500/10 p-3">{error}</p>
                            )}

                            <button onClick={handlePay} disabled={payDisabled}
                                className="w-full py-3 mt-4 bg-charcoal-accent text-charcoal-pageDark font-display text-sm hover:bg-charcoal-accentLight transition-colors btn-sweep disabled:opacity-40 disabled:cursor-not-allowed font-semibold">
                                {processing ? 'Processing…' : isStripe
                                    ? `Pay with ${selected?.label} · ${totalLabel}`
                                    : `Continue with ${selected?.label}`}
                            </button>
                            {!consent.consented && (
                                <p className="mt-3 text-charcoal-muted font-serif italic text-xs text-center">
                                    Please confirm the statements above to continue.
                                </p>
                            )}
                            <button onClick={() => navigate('/cart')}
                                className="w-full mt-3 text-charcoal-muted hover:text-charcoal-accent font-serif italic text-sm transition-colors">
                                ← Back to cart
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
